import React from 'react';
import { List } from 'immutable';
import classNames from 'classnames';
import { Paragraph, View, Block } from '@uncinc/react-drupal-core';
import config from 'react-global-configuration';
import Helmet from 'react-helmet';
import { useAspectRatio } from '../../../../helpers/useAspectRatio';

import {
  DynamicMediaItem,
  CoreLink,
  Container,
  WysiwygField,
  PatternBlock,
  Breadcrumbs,
  Share,
  Divider,
} from '../../../../components';

import i18next from '../../../../config/i18next';
import { formatDate } from '../../../../helpers/dates';
import { NALATEN_SIDEBAR_BLOCK_ID, WEBFORM_NEWSLETTER_ID } from '../../../../config/constants';

import './style.scss';

const Field = ({ label, value }) => {
  return (
    <div className="field mt-4">
      <div className="field__label">{label}:</div>
      <div className="field__value">
        {value}
      </div>
    </div>
  );
};

const NodeProjectFull = ({ id, entity, className }) => {
  const NALATEN = 'nalaten';
  const isCampaignProject = entity.get('field_campaign') && entity.get('field_campaign').size > 0;
  const campaignName = entity.getIn(['field_campaign', 0, 'entity', 'name', 0, 'value']);
  const classes = classNames(className);
  const backgroundImage = entity.getIn(['field_image', 0]);
  const ogImage = entity.getIn(['field_image', 0, 'styles', 'large']);
  const title = entity.getIn(['title', 0, 'value']);

  const lat = entity.getIn(['field_geolocation', 0, 'lat']);
  const lon = entity.getIn(['field_geolocation', 0, 'lon']);

  const zoomLevelMap = 12;
  const mapUrl = `/landkaart?lat=${lat}&lng=${lon}&mapType=project`;
  const apiKey = config.get('MAPBOX_API_KEY');
  const marker = 'https%3A%2F%2Fwww.oranjefonds.nl%2Fsites%2Fdefault%2Ffiles%2F2020-04%2Fpointer-top-aligned%402x.png';
  const mapImageUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v10/static/url-${marker}(${lon},${lat},${zoomLevelMap})/${lon},${lat},${zoomLevelMap}/300x300?access_token=${apiKey}`;
  const locationFormatted = entity.getIn(['field_address', 0, 'locality']);

  const ProjectFullClasses = classNames('NodeProjectFull', {
    'NodeProjectFull--isCampaign': isCampaignProject,
  });

  const aspectRatio = useAspectRatio({
    small: '1_1',
    medium: '2_1',
    large: '7_3',
    xlarge: '8_3',
  });

  return (
    <div className={ProjectFullClasses}>
      <Helmet>
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Container>
        <Breadcrumbs includeCurrent hideHome={isCampaignProject} />
      </Container>

      {backgroundImage && (
        <Container>
          <div className="NodeProjectFull__hero">
            <DynamicMediaItem
              className="NodeProjectFull__hero__image"
              media={backgroundImage.toJS()}
              imageAspectRatio={aspectRatio}
              alt={title}
            />
          </div>
        </Container>
      )}

      <Container medium>
        <PatternBlock className="NodeProjectFull__introduction-block">
          <h1>{title}</h1>
          <WysiwygField
            property="schema:text"
            className="node--project--body"
            html={entity.getIn(['body', 0, 'processed'], '')}
          />
        </PatternBlock>
        <article typeof="schema:WebPage" className={classes}>
          <div className="Project">
            {entity.getIn(['body', 0, 'processed']) ? (
              <div className="row">
                <div className="col-12 col-md-3">
                  {(lat && lon) && (
                    <CoreLink className="NodeProjectFull__map" to={mapUrl}>
                      <img alt={entity.getIn(['title', 0, 'value'])} src={mapImageUrl} />
                      <div className="NodeProjectFull__map__title">{i18next.t('project_see_on_map')}</div>
                    </CoreLink>

                  )}

                  {locationFormatted && (
                    <Field label={i18next.t('project_location')} value={locationFormatted} />
                  )}

                  {entity.getIn(['field_date', 0, 'value']) && (
                    <Field label={i18next.t('project_year')} value={formatDate(entity.getIn(['field_date', 0, 'value']), 'yyyy')} />
                  )}
                  {entity.getIn(['field_amount', 0, 'value']) && (
                    <Field label={i18next.t('project_amount')} value={entity.getIn(['field_amount', 0, 'value'])} />
                  )}

                  {isCampaignProject && campaignName === NALATEN && (
                    <Block id={NALATEN_SIDEBAR_BLOCK_ID} />
                  )}

                </div>
                <div className="col-xs-12 col-md-8 offset-md-1">
                  <div className="Paragraphs">
                    {
                      entity.getIn(['field_paragraphs'], List()).map((item, index) => {
                        const paragraphId = item.get('target_id');
                        const paragraph = item.getIn(['entity']);
                        return (
                          <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} containerless />
                        );
                      })
                    }
                  </div>
                  <Share title={i18next.t('project_share')} pageTitle={entity.getIn(['title', 0, 'value'])} />
                </div>
              </div>
            ) : null}
          </div>
        </article>
      </Container>
      <Divider className="related-divider" />
      <Container>
        <div key={`paragraph:${WEBFORM_NEWSLETTER_ID}`}>
          <Paragraph id={WEBFORM_NEWSLETTER_ID} display="split" />
        </div>
      </Container>
      <Divider className="related-divider" />
      <View id="related_projects" display="block_1" args={[id]} />
    </div>
  );
};

export default NodeProjectFull;
